import { Node } from '@remirror/pm/dist-types/model';
import { FromToProps, EditorState } from 'remirror';
import {
  AppliedSuggestion,
  getStoredSuggestionRanges,
} from '@replacements/hooks/floatingReplacementsToolbar/useDropdownSelectionChange';
import { replacementRegex } from '@replacements/context/phoneticReplacement';
import XRegExp from '@replacements/utils/XRegExp';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';

export enum ReplacementSourceEnum {
  Personal = 'personal',
  Team = 'team',
  UsSuggestion = 'us-suggestion',
  UkSuggestion = 'uk-suggestion',
  LlmSuggestion = 'llm-suggestion',
}

export interface FromToPropsWithReplacement extends FromToProps {
  replacement: string;
  isRespelling: boolean;
  forceUpdate?: boolean;
  text?: string;
  source?: ReplacementSourceEnum;
  isSelectedReplacementOnCommonWordDeleted: boolean;
}

export interface PhoneticReplacement {
  text: string;
  replacement: string;
  isRespelling: boolean;
  enabled: boolean;
  source?: ReplacementSourceEnum;
}

/**
 * Removes marks from the document
 */
const getSantizedDoc = (state: EditorState): Node => {
  let { tr } = state;
  tr = tr.removeMark(0, state.doc.nodeSize - 2);
  return tr.doc;
};

const isSuggestion = (source?: string) =>
  source?.toLowerCase().includes('suggestion');

const convertToAbsolutePosition = (
  replacement: FromToPropsWithReplacement,
  nodeStartPos: number
): FromToPropsWithReplacement => {
  const from = nodeStartPos + replacement.from;
  const to = from + (replacement.to - replacement.from);
  return {
    ...replacement,
    from,
    to,
  };
};

const isAppliedSuggestionMatch = (
  replacement: FromToPropsWithReplacement,
  applied: AppliedSuggestion[]
) => {
  if (!isSuggestion(replacement.source)) return true;

  return applied.some(
    suggestion =>
      suggestion.text?.toLowerCase() === replacement.text?.toLowerCase() &&
      suggestion.replacement === replacement.replacement &&
      suggestion.from === replacement.from &&
      suggestion.to === replacement.to
  );
};

const shouldForceUpdate = (
  replacement: FromToPropsWithReplacement,
  state: EditorState,
  fullLibrary: PhoneticReplacement[]
) => {
  const slice = state.doc.slice(replacement.from, replacement.to);
  const existingNode = slice.content.firstChild;

  const existingMark = existingNode?.marks.find(
    m => m.type === state.schema.marks.phonetic
  );

  if (existingMark?.attrs.replacementChanged) {
    return !fullLibrary.some(
      r =>
        r.text.toLowerCase() === existingNode?.textContent.toLowerCase() &&
        r.replacement === existingMark.attrs.replacement
    );
  }

  return false;
};

/**
 * Search through the remirror doc object for each text node
 * and find the position of the replacements to be made
 */
export const getPhoneticReplacements = (
  state: EditorState,
  replacementsLibrary: EnhancedPhoneticReplacement[],
  oxfordLibrary: PhoneticReplacement[],
  llmLibrary: PhoneticReplacement[],
  projectId: string
) => {
  let replacements: FromToPropsWithReplacement[] = [];
  const appliedSuggestionRanges: AppliedSuggestion[] =
    getStoredSuggestionRanges(projectId);

  const fullLibrary = [...replacementsLibrary, ...oxfordLibrary, ...llmLibrary];
  const sanitizedDoc = getSantizedDoc(state);
  const wordWithSelectedReplacementDeleted: (string | undefined)[] = [];

  sanitizedDoc.descendants((node, pos) => {
    if (!node.isText) return;

    const replacementsWithinNode = getReplacementPositions(
      replacementsLibrary,
      oxfordLibrary,
      llmLibrary,
      node.textContent
    );

    const processedReplacements = replacementsWithinNode
      .map(rep => convertToAbsolutePosition(rep, pos))
      .filter(rep => isAppliedSuggestionMatch(rep, appliedSuggestionRanges))
      .map(rep => ({
        ...rep,
        forceUpdate: shouldForceUpdate(rep, state, fullLibrary),
      }));

    for (const replacement of processedReplacements) {
      if (replacement.forceUpdate) {
        wordWithSelectedReplacementDeleted.push(replacement.text);
      }
    }

    replacements = replacements.concat(
      processedReplacements.map(rep => ({
        ...rep,
        isSelectedReplacementOnCommonWordDeleted:
          wordWithSelectedReplacementDeleted.includes(rep.text),
      }))
    );
  });

  return replacements;
};

/**
 * This method will filter the replacements by first occurence and remove any duplicates that follow.
 */
export const getReplacementPositions = (
  replacementsLibrary: EnhancedPhoneticReplacement[],
  oxfordLibrary: PhoneticReplacement[],
  llmLibrary: PhoneticReplacement[],
  text: string
): FromToPropsWithReplacement[] => {
  let replacementPositions: FromToPropsWithReplacement[] = [];

  const taggedLibrary = [
    ...oxfordLibrary,
    ...llmLibrary,
    ...replacementsLibrary.map(r => ({
      ...r,
      source: r.libraryId
        ? ReplacementSourceEnum.Team
        : ReplacementSourceEnum.Personal,
    })),
  ];

  const filtered: Record<string, PhoneticReplacement> = {};
  taggedLibrary.forEach(item => {
    if (!filtered[item.text.toLowerCase()]) {
      filtered[item.text.toLowerCase()] = item;
    }
  });

  const filteredReplacements = Object.entries(filtered);

  filteredReplacements.forEach(([, replacement]) => {
    const regex = replacementRegex(replacement.text);

    const positions = XRegExp.getLbIgnoreRespellingsPositions(text, regex);
    replacementPositions = replacementPositions.concat(
      positions.map(index => ({
        text: replacement.text,
        replacement: replacement.replacement,
        from: index.from,
        to: index.to,
        isRespelling: replacement.isRespelling,
        source: replacement.source,
        isSelectedReplacementOnCommonWordDeleted: false,
      }))
    );
  });

  return replacementPositions;
};

export default {
  getPhoneticReplacements,
  getReplacementPositions,
};
